/**
 * themeRegistry — 主题引擎 v2 (SPEC_UI_UX_UPGRADE T2-6)
 *
 * 单源：style.css 的 `:root`（浅色基础令牌）与 `[data-theme="dark"], .dark`
 * （暗色令牌块；发布物见 src/generated/design-tokens.json）。本注册表负责：
 *  1. 内置主题（minimal 宣纸白 / cyber 玄渊黑）的 DOM 侧应用（.dark class + data-theme 双保险）；
 *  2. 派生包 provider 接口：行业包可 `registerTheme()` 注入自有主题。
 *     **两条注入路径，按 `mode` 分工**（此前注释只写了第二条，与实现不符，被单测照出）：
 *       · `mode: 'light' | 'dark'` —— 继承基础令牌块（`data-theme` 落 light/dark，
 *         同时拿到 `[data-theme="light"] …` 那批组件级覆盖），包不必自带色板；
 *       · `mode: <其它字符串>`（自定义档，如 'sepia'）—— 以 **id** 落 `data-theme`，
 *         包必须在 CSS 侧提供 `[data-theme="<id>"]` 覆盖块；平台不改。
 *  3. legacy localStorage 三键同步（omni_theme / user_theme / nym_theme）。
 */

export interface ThemeDef {
    id: string;
    /** 见文件头：'light' | 'dark' 继承基础令牌块；其它字符串 = 自定义档，需自带 [data-theme="<id>"] 块 */
    mode: 'light' | 'dark' | string;
    /** i18n key（展示名交由消费方翻译，本表不存文案） */
    labelKey?: string;
    /** 派生包来源标记 */
    source?: 'builtin' | 'plugin';
}

const themes = new Map<string, ThemeDef>();

function builtinDefaults(): void {
    themes.set('minimal', { id: 'minimal', mode: 'light', labelKey: 'settings.theme_light', source: 'builtin' });
    themes.set('cyber', { id: 'cyber', mode: 'dark', labelKey: 'settings.theme_dark', source: 'builtin' });
}
builtinDefaults();

// 当期默认主题 = 浅色（minimal/宣纸白）。2026-09-23 之前是 cyber/玄渊黑。
// 改默认只改这一个常量（外加 index.html 首屏脚本里的版本戳）：
// 下面两处「未知主题」的回落点都引用它，免得出现「默认换了、回落点还指向旧主题」的半截状态。
export const DEFAULT_THEME_ID = 'minimal';

/**
 * 本会话内最后一次 applyTheme 生效的主题 id。
 *
 * 为什么不能从 DOM 反推：`data-theme` 只编码"档"（light/dark），不编码 id ——
 * 同一个 `data-theme="dark"` 可能是内置 cyber，也可能是任何暗色派生主题；
 * 而"注销一个**没在用**的派生主题"必须与"注销正在用的那个"分开，
 * 旧的 `data-theme === def.mode` 判断会把前者误判成后者（卸载一个没人用的暗色包，
 * 正在用的 cyber 被顺手改回默认主题）。activeThemeId 是唯一能区分两者的读数。
 */
let activeThemeId: string | null = null;

/** 当前生效的主题 id。本会话未 applyTheme 过时（首屏由 index.html 先定），按 DOM 的 data-theme 反推。 */
export function getActiveThemeId(): string {
    if (activeThemeId) return activeThemeId;
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'cyber' : DEFAULT_THEME_ID;
}

export function registerTheme(def: ThemeDef): () => void {
    themes.set(def.id, { ...def, source: def.source || 'plugin' });
    return () => {
        // 派生包卸载时注销；若**正在用的就是它**才回退默认主题（见 activeThemeId 的注释）。
        const wasActive = getActiveThemeId() === def.id;
        themes.delete(def.id);
        if (wasActive) {
            applyTheme(DEFAULT_THEME_ID);
        }
    };
}

export function listThemes(): ThemeDef[] {
    return [...themes.values()];
}

export function getTheme(id: string): ThemeDef | undefined {
    return themes.get(id);
}

/**
 * 应用主题到 DOM。mode==='dark' 的主题加 .dark class（Tailwind dark: 变体依赖）。
 * 未知 id 回落默认主题（DOM 与 activeThemeId 一起回落，不留下「状态说 A、屏面是 B」）。
 */
export function applyTheme(id: string): void {
    const def = themes.get(id) || themes.get(DEFAULT_THEME_ID)!;
    const root = document.documentElement;
    activeThemeId = def.id;
    if (def.mode === 'dark') {
        root.classList.add('dark');
        root.setAttribute('data-theme', 'dark');
    } else {
        root.classList.remove('dark');
        root.setAttribute('data-theme', def.mode === 'light' ? 'light' : def.id);
    }
}

/** legacy 三键同步（omni_theme 为主键，user_theme/nym_theme 为兼容镜像） */
export function syncLegacyThemeStorage(id: string): void {
    const def = themes.get(id);
    const legacy = def?.mode === 'dark' ? 'dark' : 'light';
    try {
        localStorage.setItem('user_theme', legacy);
        localStorage.setItem('nym_theme', legacy);
    } catch { /* 私密模式等场景静默 */ }
}
